import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  SmartToy as SmartToyIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { atendimentoService, AtendimentoContato, AtendimentoTicket } from '../../../services/atendimentoService';
import { clientService } from '../../../services/clientService';
import contractService from '../../../services/contractService';
import tagService from '../../../services/tagService';
import { Client, Contract, Tag } from '../../../types';
import VisualizarTicketDialog from './VisualizarTicketDialog';

interface ContatoAtendimentoDetalhesDialogProps {
  open: boolean;
  contato: AtendimentoContato | null;
  onClose: () => void;
}

const getInitials = (name: string) => name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';

const formatDateTime = (value?: string | null) => value ? new Date(value).toLocaleString('pt-BR') : '-';

const formatStatusLabel = (status?: string | null) => status ? status.replace(/_/g, ' ') : '-';

const getClientStatusColor = (status?: Client['status']) => {
  switch (status) {
    case 'ativo':
      return 'success';
    case 'inadimplente':
    case 'bloqueado':
      return 'error';
    case 'suspenso':
      return 'warning';
    default:
      return 'default';
  }
};

const getTicketStatusColor = (status: AtendimentoTicket['status']) => {
  switch (status) {
    case 'em_atendimento':
      return 'success';
    case 'pendente':
      return 'warning';
    case 'fechado_bot':
      return 'info';
    default:
      return 'default';
  }
};

const ContatoAtendimentoDetalhesDialog: React.FC<ContatoAtendimentoDetalhesDialogProps> = ({
  open,
  contato,
  onClose,
}) => {
  const navigate = useNavigate();
  const [cliente, setCliente] = useState<Client | null>(null);
  const [contratos, setContratos] = useState<Contract[]>([]);
  const [tickets, setTickets] = useState<AtendimentoTicket[]>([]);
  const [ticketVisualizacao, setTicketVisualizacao] = useState<AtendimentoTicket | null>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [modalFotoAberto, setModalFotoAberto] = useState(false);

  const clienteId = contato?.cliente_id || contato?.cliente?.id || null;

  useEffect(() => {
    if (!open || !contato) return;

    let ativo = true;
    const carregarDados = async () => {
      setLoading(true);
      setErro('');
      setCliente(null);
      setContratos([]);
      setTickets([]);

      try {
        const ticketsPromise = atendimentoService.getTickets({ contato_id: contato.id });
        const clientePromise = clienteId ? clientService.getById(clienteId) : Promise.resolve(null);
        const contratosPromise = clienteId ? contractService.getByClientId(clienteId, { limit: 100 }) : Promise.resolve(null);
        const tagsPromise = tagService.getAll({ limit: 1000 }).catch(() => null);

        const [ticketsData, clienteData, contratosData, tagsData] = await Promise.all([
          ticketsPromise,
          clientePromise,
          contratosPromise,
          tagsPromise,
        ]);

        if (!ativo) return;
        setTickets(ticketsData || []);
        setCliente(clienteData);
        setContratos(contratosData?.data || []);
        if (tagsData?.success && tagsData?.data) {
          setAllTags(tagsData.data);
        }
      } catch (error: any) {
        if (!ativo) return;
        setErro(error?.response?.data?.message || error?.message || 'Erro ao carregar detalhes do contato.');
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregarDados();
    return () => {
      ativo = false;
    };
  }, [open, contato?.id, clienteId]);

  const contactTags = React.useMemo(() => {
    if (!contato?.tags) return [];
    let rawArray: any[] = [];
    if (Array.isArray(contato.tags)) {
      rawArray = contato.tags;
    } else if (typeof contato.tags === 'string') {
      try {
        const parsed = JSON.parse(contato.tags);
        if (Array.isArray(parsed)) rawArray = parsed;
      } catch (e) {
        rawArray = [];
      }
    }
    const ids = rawArray.map((v) => typeof v === 'object' && v !== null ? Number(v.id) : Number(v)).filter((n) => !isNaN(n));
    return allTags.filter((t) => ids.includes(t.id));
  }, [contato?.tags, allTags]);

  useEffect(() => {
    if (open) return;
    setCliente(null);
    setContratos([]);
    setTickets([]);
    setTicketVisualizacao(null);
    setErro('');
    setModalFotoAberto(false);
  }, [open]);

  const nomeContato = contato?.nome || contato?.cliente?.nome || 'Contato';
  const dadosCliente: Array<[string, string | number | null | undefined]> = cliente
    ? [
      ['Nome', cliente.nome],
      ['Status', formatStatusLabel(cliente.status)],
      ['CPF/CNPJ', cliente.cpf_cnpj],
      ['Cidade', cliente.cidade && cliente.estado ? `${cliente.cidade}-${cliente.estado}` : cliente.cidade],
      ['Endereco', [cliente.endereco, cliente.numero].filter(Boolean).join(', ')],
      ['Bairro', cliente.bairro],
      ['Complemento', cliente.complemento],
      ...(cliente.observacoes?.trim() ? [['Observacoes', cliente.observacoes] as [string, string]] : []),
    ]
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      }}
    >
      <DialogTitle sx={{ py: 1.25 }}>Detalhes do contato</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {!contato ? null : (
          <>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  src={atendimentoService.getAvatarUrl(contato.foto_perfil)}
                  onClick={() => {
                    if (contato.foto_perfil) {
                      setModalFotoAberto(true);
                    }
                  }}
                  sx={{
                    width: 58,
                    height: 58,
                    fontSize: '1.1rem',
                    bgcolor: '#26a69a',
                    cursor: contato.foto_perfil ? 'pointer' : 'default',
                  }}
                >
                  {!contato.foto_perfil && getInitials(nomeContato)}
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle1" fontWeight={800} noWrap>{nomeContato}</Typography>
                  <Box display="flex" alignItems="center" gap={0.6} flexWrap="wrap">
                    <WhatsAppIcon sx={{ fontSize: 16, color: '#25d366' }} />
                    <Typography variant="body2" color="text.secondary">{contato.numero}</Typography>
                    {contato.cliente_id && <Chip size="small" color="primary" variant="outlined" label="Cliente vinculado" sx={{ height: 20, fontSize: '0.68rem' }} />}
                    {contato.is_grupo && <Chip size="small" variant="outlined" label="Grupo" sx={{ height: 20, fontSize: '0.68rem' }} />}
                  </Box>
                  <Typography variant="caption" color={contato.bot_desativado ? 'error.main' : 'success.main'} fontWeight={700} display="block">
                    <SmartToyIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.4 }} />
                    {contato.bot_desativado ? 'Bot desativado' : 'Bot ativado'}
                  </Typography>
                  {contactTags.length > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={0.5}>
                      {contactTags.map((t) => (
                        <Chip
                          key={t.id}
                          label={t.titulo}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            bgcolor: alpha(t.color, 0.15),
                            color: t.color,
                            border: `1px solid ${alpha(t.color, 0.3)}`,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider />

            {erro && <Alert severity="error" sx={{ m: 2 }}>{erro}</Alert>}

            <Grid container>
              <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
                <Box sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
                    <Typography variant="subtitle2" fontWeight={800}>Dados do cliente</Typography>
                    {clienteId && (
                      <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/clients/${clienteId}`)} sx={{ textTransform: 'none' }}>
                        Ver cliente
                      </Button>
                    )}
                  </Box>

                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1} py={2}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">Carregando...</Typography>
                    </Box>
                  ) : !clienteId ? (
                    <Typography variant="body2" color="text.secondary">Nenhum cliente vinculado.</Typography>
                  ) : !cliente ? (
                    <Typography variant="body2" color="text.secondary">Cliente nao encontrado.</Typography>
                  ) : (
                    <>
                      {dadosCliente.map(([key, value]) => (
                        <Box key={key} display="flex" justifyContent="space-between" gap={1} py={0.55} sx={{ borderBottom: '1px dashed', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">{key}</Typography>
                          {key === 'Status' ? (
                            <Chip label={value || '-'} color={getClientStatusColor(cliente.status) as any} size="small" sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                          ) : (
                            <Typography variant="caption" fontWeight={700} textAlign="right" sx={{ wordBreak: 'break-word' }}>{value || '-'}</Typography>
                          )}
                        </Box>
                      ))}

                      <Typography variant="subtitle2" fontWeight={800} mt={2} mb={0.75}>Contratos</Typography>
                      {contratos.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">Nenhum contrato encontrado.</Typography>
                      ) : (
                        contratos.map((contrato) => (
                          <Box key={contrato.id} display="flex" alignItems="center" justifyContent="space-between" gap={1} py={0.55} sx={{ borderBottom: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="caption" fontWeight={700} noWrap>{contrato.titulo || `Contrato #${contrato.id}`}</Typography>
                            <Chip label={formatStatusLabel(contrato.status)} size="small" sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                          </Box>
                        ))
                      )}
                    </>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={800} mb={1}>Tickets do contato</Typography>
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1} py={2}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">Carregando tickets...</Typography>
                    </Box>
                  ) : tickets.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nenhum ticket encontrado para este contato.</Typography>
                  ) : (
                    <List disablePadding sx={{ maxHeight: 390, overflowY: 'auto' }}>
                      {tickets.map((ticket) => (
                        <ListItemButton key={ticket.id} onClick={() => setTicketVisualizacao(ticket)} sx={{ px: 0.5, py: 0.8, borderBottom: '1px dashed', borderColor: 'divider' }}>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <Typography variant="body2" fontWeight={800}>#{ticket.id}</Typography>
                                <Chip label={formatStatusLabel(ticket.status)} color={getTicketStatusColor(ticket.status) as any} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                {ticket.status !== 'fechado' && ticket.status !== 'fechado_bot' && <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main' }} />}
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography component="span" variant="caption" color="text.secondary" display="block" noWrap>
                                  {ticket.ultima_mensagem || 'Sem ultima mensagem'}
                                </Typography>
                                <Typography component="span" variant="caption" color="text.disabled" display="block">
                                  Criado: {formatDateTime(ticket.createdAt)}
                                  {ticket.data_fechamento ? ` | Fechado: ${formatDateTime(ticket.data_fechamento)}` : ''}
                                </Typography>
                              </>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>

      <VisualizarTicketDialog
        open={Boolean(ticketVisualizacao)}
        ticket={ticketVisualizacao}
        onClose={() => setTicketVisualizacao(null)}
        onTicketReopened={(ticket) => {
          setTickets((prev) => prev.map((item) => item.id === ticket.id ? ticket : item));
          setTicketVisualizacao(null);
          onClose();
          navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
        }}
      />

      <Dialog
        open={modalFotoAberto}
        onClose={() => setModalFotoAberto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <IconButton
            onClick={() => setModalFotoAberto(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {contato?.foto_perfil && (
            <img
              src={atendimentoService.getAvatarUrl(contato.foto_perfil)}
              alt="Foto do Contato"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ContatoAtendimentoDetalhesDialog;
